import { useCallback, useEffect, useRef, useState } from 'react';
import type { StopFinishAudit } from '../services/api';
import { decryptStopBootstrapCipher } from '../utils/stopBootstrapDecrypt';
import {
  loadStopAssemblyExports,
  type StopWasmExports,
} from '../utils/stopWasmExports';

export interface StopSubmissionResultDto {
  correct: boolean;
  duplicate: boolean;
  foundCount: number;
  wrongCount: number;
  questionComplete: boolean;
  questionFailed: boolean;
  questionPointsSoFar: number;
  pointsEarned: number;
  requiredCount: number;
  maxWrong: number;
  revealedAnswer?: {
    answerId: string;
    text: string;
    textEn: string;
    imageAssetKey: string;
  } | null;
}

const WASM_BASE =
  (process.env.VITE_STOP_WASM_BASE ?? '/wasm/stop').replace(/\/$/, '');

let dotnetModulePromise: Promise<{
  getAssemblyExports: (name: string) => Promise<Record<string, unknown>>;
  getConfig: () => { mainAssemblyName: string };
}> | null = null;

async function loadWasmExports(): Promise<StopWasmExports> {
  if (!dotnetModulePromise) {
    dotnetModulePromise = (async () => {
      const dotnetUrl = `${WASM_BASE}/_framework/dotnet.js`;
      const bootConfigUrl = `${WASM_BASE}/_framework/blazor.boot.json`;
      const mod = await import(/* webpackIgnore: true */ dotnetUrl);
      const dotnet = mod.dotnet as
        | {
            withConfigSrc: (src: string) => { create: () => Promise<unknown> };
            create: () => Promise<unknown>;
          }
        | undefined;
      if (!dotnet?.create) {
        throw new Error('Stop WASM: dotnet runtime not found.');
      }
      const runtime = dotnet.withConfigSrc
        ? await dotnet.withConfigSrc(bootConfigUrl).create()
        : await dotnet.create();
      return runtime as {
        getAssemblyExports: (name: string) => Promise<Record<string, unknown>>;
        getConfig: () => { mainAssemblyName: string };
      };
    })();
  }

  const runtime = await dotnetModulePromise;
  const config = runtime.getConfig();
  return loadStopAssemblyExports(runtime.getAssemblyExports, config.mainAssemblyName);
}

export function useStopWasm(engineBootstrapCipher: string | undefined) {
  const exportsRef = useRef<StopWasmExports | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!engineBootstrapCipher?.trim()) {
      setReady(false);
      setError(null);
      return;
    }

    setReady(false);
    setError(null);

    (async () => {
      try {
        const wasm = await loadWasmExports();
        if (cancelled) return;
        const keyB64 = wasm.getBootstrapKeyBase64();
        const plainB64 = await decryptStopBootstrapCipher(engineBootstrapCipher, keyB64);
        if (cancelled) return;
        const initResult = wasm.initializeSessionFromPlaintext(plainB64);
        const initError =
          typeof initResult === 'string' ? initResult.trim() : 'Failed to initialize Stop engine.';
        if (initError) {
          setError(initError);
          setReady(false);
          return;
        }
        exportsRef.current = wasm;
        setError(null);
        setReady(true);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Stop WASM load failed.');
        setReady(false);
      }
    })();

    return () => {
      cancelled = true;
      exportsRef.current?.disposeSession();
      exportsRef.current = null;
      setReady(false);
    };
  }, [engineBootstrapCipher]);

  const submitAnswer = useCallback(
    async (questionId: string, text: string): Promise<StopSubmissionResultDto | null> => {
      const wasm = exportsRef.current;
      if (!wasm) return null;
      const json = wasm.submitAnswer(questionId, text);
      return JSON.parse(json) as StopSubmissionResultDto;
    },
    []
  );

  const buildFinishPayload = useCallback((): StopFinishAudit | null => {
    const wasm = exportsRef.current;
    if (!wasm) return null;
    const json = wasm.buildFinishPayload();
    if (!json || json === '{}') return null;
    return JSON.parse(json) as StopFinishAudit;
  }, []);

  return {
    ready,
    error,
    submitAnswer,
    buildFinishPayload,
  };
}
